import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

import { hasRole } from '@/auth/roles';
import { useAuth } from '@/auth/useAuth';
import { useI18n } from '@/i18n/useI18n';
import { cn } from '@/ui/cn';
import { groupedModules, type NavModule } from './nav';

/**
 * Navegación, agrupada y en el violeta de la marca.
 *
 * **Por qué grupos.** Dieciséis módulos en una lista plana obligan a leerlos
 * todos para encontrar uno. Los encabezados son los del trabajo real —catálogo,
 * planta, actividad, protección, configuración— y el panel de XYVONA va al
 * final, separado, porque no es del cliente.
 *
 * **Por qué fondo claro y no violeta pleno.** Un panel de 240px en Deep Purple
 * deja los encabezados sin margen para destacarse: sobre violeta todo es blanco
 * a distinta opacidad, y la única diferencia posible entre "título" y "módulo"
 * pasa a ser el peso de la tipografía. Sobre fondo claro el título usa la tinta
 * más oscura del sistema y el módulo una intermedia, y esa distancia es la que
 * hace que se lea como encabezado. La marca sigue estando donde el manual la
 * quiere: el item activo. Los tokens y sus contrastes, en `styles/global.css`.
 *
 * **El item activo no se marca sólo con color.** Lleva barra a la izquierda y
 * `aria-current`: distinguir "activo" de "hover" por tono es justo lo que no
 * funciona con poca luz o con daltonismo.
 *
 * Un módulo `pending` se renderiza como texto, no como enlace: deshabilitar un
 * `<a>` con CSS lo deja igualmente enfocable y clicable con teclado, que es la
 * forma clásica de que un "botón deshabilitado" no lo esté. Con
 * `<span aria-disabled>` el orden de tabulación lo saltea y el lector de
 * pantalla anuncia el estado.
 */
export function Sidebar({ onNavigate }: { onNavigate?: () => void }): ReactNode {
  const { t } = useI18n();
  const { claims } = useAuth();
  const groups = groupedModules(claims?.role, hasRole, claims?.tenant);

  return (
    <nav
      aria-label={t('nav.title')}
      className="flex h-full flex-col gap-6 border-r border-sidebar-line bg-sidebar px-3 py-4 text-sidebar-ink"
    >
      {groups.map((group) => (
        <div key={group.section}>
          {group.labelKey !== null && (
            <p
              // `id` para que el <ul> de abajo lo referencie: sin eso el lector
              // de pantalla anuncia siete listas sin nombre.
              id={`nav-section-${group.section}`}
              className="px-3 pb-2 text-2xs font-bold tracking-[0.1em] text-sidebar-heading uppercase"
            >
              {t(group.labelKey)}
            </p>
          )}
          <ul
            className="flex flex-col gap-0.5"
            {...(group.labelKey !== null
              ? { 'aria-labelledby': `nav-section-${group.section}` }
              : {})}
          >
            {group.modules.map((module) => (
              <li key={module.id}>
                <ModuleLink module={module} onNavigate={onNavigate} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function ModuleLink({
  module,
  onNavigate,
}: {
  module: NavModule;
  // `| undefined` explícito: con `exactOptionalPropertyTypes` una prop
  // opcional que se pasa hacia abajo necesita admitir el `undefined`.
  onNavigate?: (() => void) | undefined;
}): ReactNode {
  const { t } = useI18n();
  const label = t(module.labelKey);

  if (module.status === 'pending') {
    return (
      <span
        aria-disabled="true"
        title={t('nav.pendingHint')}
        className="flex items-center justify-between gap-2 rounded-(--radius-control) px-3 py-2 text-sm text-sidebar-ink-soft opacity-80"
      >
        <span>{label}</span>
        <span className="rounded-full border border-sidebar-line px-1.5 py-px text-2xs tracking-wide uppercase">
          {t('nav.pending')}
        </span>
      </span>
    );
  }

  return (
    <NavLink
      to={module.path}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'relative flex items-center gap-2 rounded-(--radius-control) px-3 py-2 text-sm font-medium transition-colors',
          // La barra de la izquierda es un pseudo-elemento y no un borde: un
          // borde corre el texto 3px y hace saltar la fila al activarse.
          isActive
            ? 'bg-sidebar-active font-semibold text-sidebar-active-ink before:absolute before:top-1.5 before:bottom-1.5 before:-left-0.5 before:w-[3px] before:rounded-full before:bg-sidebar-bar before:content-[""]'
            : 'text-sidebar-ink hover:bg-sidebar-hover hover:text-sidebar-active-ink',
        )
      }
    >
      {label}
    </NavLink>
  );
}
